import { useState, useEffect } from "react";
import { ActiveTest } from "../test/ActiveTest";
import { CreateTestDialog } from "@/components/car-test/CreateTestDialog";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import CreateTestButton from "@/components/car-test/CreateTestButton";
import { ArchiveRestore, Check, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AnimatePresence, motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ColoredOutlineButton,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import {
  generateVehicleTestPdf,
  resolveTestImageUrls,
} from "@/utils/pdfGenerator";
import { useMedia } from "@/hooks/useMedia";
import { VehicleTestDocumentView } from "./VehicleTestDocumentView";
import { useTest } from "@/hooks/useTest";
import { VehicleTest } from "@/types/vehicleTest";
import { toast } from "sonner";
import { PdfGenerationDialog } from "@/components/ui/PdfGenerationDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import { useNavigate } from "react-router-dom";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";

function mapVehicleTestToDocumentData(test: VehicleTest) {
  return test;
}

const AdminCarTestArchive = () => {
  const [viewTest, setViewTest] = useState<VehicleTest | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const isMobile = useIsMobile();
  const showPageBack = useShowNavigateBack();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<{
    isOpen: boolean;
    testName?: string;
    fileName?: string;
  }>({
    isOpen: false,
  });
  // Use the test hook to fetch archived tests
  const {
    archivedTests,
    isLoadingArchivedTests,
    refetchArchivedTests,
    unarchiveCarTests,
    isUnarchivingCarTests,
  } = useTest({ tab: "archived" });

  const { getDownloadUrlAsync } = useMedia();

  const handleCreateTest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleDownloadPdf = async (test: VehicleTest) => {
    setIsGeneratingPdf({
      isOpen: true,
      testName: test.vehicleName,
      fileName: `fordonsprotokoll-${test.vehicleDetails?.regNumber || test.id}.pdf`,
    });
    try {
      const testWithUrls = await resolveTestImageUrls(
        test,
        getDownloadUrlAsync,
      );
      await generateVehicleTestPdf(testWithUrls, language);
      toast.success(ct.messages.pdfGenerated);
    } catch (error) {
      toast.error(ct.messages.pdfError);
    } finally {
      setIsGeneratingPdf({ isOpen: false });
    }
  };

  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [isUnarchiveMode, setIsUnarchiveMode] = useState(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);

  const handleUnarchive = async (testIds: string[]) => {
    try {
      await unarchiveCarTests(testIds);
      toast.success(ct.archive.unarchived);
      refetchArchivedTests();
      setSelectedTests(new Set());
      setIsUnarchiveMode(false);
    } catch (error) {
      toast.error(ct.archive.unarchiveError);
    }
  };

  // Show loading state
  if (isLoadingArchivedTests) {
    return (
      <div>
        {showPageBack && <BtnNavigateBack label="Dashboard" />}
        <div className="flex justify-center mb-4">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">
            {ct.archive.title}
          </h1>
        </div>
        <div className="container max-w-4xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">{ct.archive.loading}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showPageBack && <BtnNavigateBack label="Dashboard" />}

      <div className="relative flex justify-center items-center mb-4">
        <h1 className="text-xl md:text-2xl font-bold tracking-tight">
          {ct.archive.title}
        </h1>
        {!isMobile && (
          <div className="absolute left-2">
            <ColoredOutlineButton
              type="button"
              variant="blue"
              tintedBackground
              size="sm"
              onClick={() => navigate("/admin/car-test")}
              className="h-8 px-2.5 text-xs gap-1.5"
            >
              <ClipboardList
                className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                strokeWidth={2.35}
              />
              <span>{ct.archive.backToTests}</span>
            </ColoredOutlineButton>
          </div>
        )}
        <CreateTestButton
          onClick={handleCreateTest}
          className="absolute right-2 !w-auto"
        />
      </div>

      <div className="container max-w-4xl mx-auto px-4">
        <div className="space-y-4">
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
            <Input
              placeholder={ct.search.placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="backdrop-blur w-full text-[10px] sm:text-sm h-8 sm:h-10 md:min-w-0 md:flex-1"
            />
            <div
              className={cn(
                "flex gap-2 items-stretch",
                isMobile ? "w-full" : "w-auto shrink-0 flex-wrap justify-end",
              )}
            >
              <ColoredOutlineButton
                type="button"
                variant="neutral"
                tintedBackground
                size="sm"
                onClick={() => {
                  setIsUnarchiveMode(!isUnarchiveMode);
                  setSelectedTests(new Set());
                }}
                className={cn(
                  "h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap",
                  isMobile ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
                )}
              >
                <ArchiveRestore
                  className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                  strokeWidth={2.35}
                />
                <span className="truncate">
                  {isUnarchiveMode
                    ? ct.archive.exitUnarchiveMode
                    : isMobile
                      ? ((ct.archive as { unarchiveModeShort?: string })
                          .unarchiveModeShort ??
                        ct.archive.buttonLabel ??
                        "Arkiv")
                      : ct.archive.unarchiveMode}
                </span>
              </ColoredOutlineButton>
              {isUnarchiveMode && selectedTests.size > 0 ? (
                <ColoredOutlineButton
                  type="button"
                  variant="success"
                  tintedBackground
                  size="sm"
                  onClick={() => setShowUnarchiveDialog(true)}
                  className={cn(
                    "h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap",
                    isMobile ? "flex-1 min-w-0 w-full" : "shrink-0 w-auto",
                  )}
                >
                  <Check
                    className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                  <span className="truncate">
                    {ct.archive.unarchiveSelected.replace(
                      "{count}",
                      String(selectedTests.size),
                    )}
                  </span>
                </ColoredOutlineButton>
              ) : isMobile ? (
                <ColoredOutlineButton
                  type="button"
                  variant="blue"
                  tintedBackground
                  size="sm"
                  onClick={() => navigate("/admin/car-test")}
                  className="h-8 px-2.5 text-xs gap-1.5 justify-center whitespace-nowrap flex-1 min-w-0 w-full"
                >
                  <ClipboardList
                    className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                  <span className="truncate">
                    {ct.archive.backToTestsMobile}
                  </span>
                </ColoredOutlineButton>
              ) : null}
            </div>
          </div>
          <div className="space-y-4">
            <AnimatePresence>
              {archivedTests && archivedTests.length > 0 ? (
                archivedTests
                  .filter((test) => {
                    const searchLower = search.toLowerCase();
                    return (
                      (
                        test.vehicleDetails.regNumber?.toLowerCase() || ""
                      ).includes(searchLower) ||
                      (test.vehicleName?.toLowerCase() || "").includes(
                        searchLower,
                      )
                    );
                  })
                  .map((test) => (
                    <motion.div
                      key={test.id}
                      className={`relative ${
                        isUnarchiveMode ? "cursor-pointer" : ""
                      }`}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 200 }}
                      transition={{ duration: 0.5 }}
                      onClick={() => {
                        if (isUnarchiveMode) {
                          const newSelected = new Set(selectedTests);
                          if (newSelected.has(test.id)) {
                            newSelected.delete(test.id);
                          } else {
                            newSelected.add(test.id);
                          }
                          setSelectedTests(newSelected);
                        }
                      }}
                    >
                      <div
                        className={`flex items-start gap-3 ${
                          isUnarchiveMode ? "mb-2" : ""
                        }`}
                      >
                        {isUnarchiveMode && (
                          <input
                            type="checkbox"
                            checked={selectedTests.has(test.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newSelected = new Set(selectedTests);
                              if (e.target.checked) {
                                newSelected.add(test.id);
                              } else {
                                newSelected.delete(test.id);
                              }
                              setSelectedTests(newSelected);
                            }}
                            className="w-5 h-5 mt-2 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="flex-1">
                          <ActiveTest
                            key={test.vehicleDetails.regNumber}
                            test={test}
                            onView={() => setViewTest(test)}
                            onDownload={handleDownloadPdf}
                            isAdmin={true}
                            isUpdating={isUnarchivingCarTests}
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {ct.archive.noArchivedTests}
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <CreateTestDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      {viewTest && (
        <Dialog open={!!viewTest} onOpenChange={() => setViewTest(null)}>
          <DialogContent className="rounded-lg max-w-4xl max-h-[75vh] sm:max-h-[80vh] overflow-hidden flex flex-col p-0">
            <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
              <DialogTitle>
                {[
                  viewTest.vehicleDetails?.make,
                  viewTest.vehicleDetails?.model,
                  viewTest.vehicleDetails?.year,
                ]
                  .filter(Boolean)
                  .join(" ") || ct.dialogs.view.title}
              </DialogTitle>
            </DialogHeader>
            <div className="px-4 sm:px-6 pb-6 overflow-y-auto flex-1 min-h-0">
              <VehicleTestDocumentView
                data={mapVehicleTestToDocumentData(viewTest)}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      <PdfGenerationDialog
        isOpen={isGeneratingPdf.isOpen}
        testName={isGeneratingPdf.testName}
        fileName={isGeneratingPdf.fileName}
      />

      {/* Unarchive confirmation dialog */}
      <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ct.archive.unarchiveDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              {ct.archive.unarchiveDialog.message.replace(
                "{count}",
                String(selectedTests.size),
              )}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUnarchiveDialog(false)}
            >
              {ct.archive.unarchiveDialog.cancel}
            </Button>
            <Button
              onClick={() => {
                handleUnarchive(Array.from(selectedTests));
                setShowUnarchiveDialog(false);
              }}
              disabled={isUnarchivingCarTests}
            >
              {ct.archive.unarchiveDialog.confirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCarTestArchive;
